import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  fetchNetworks,
  fetchAlgorithms,
  fetchNetworkInfo,
  fetchNetworkTopology,
  runBenchmark,
} from "../lib/doeApi";
import { streamDOE } from "../lib/doeStream";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import NetworkTopology from "../components/doe/NetworkTopology";
import DOEChart from "../components/doe/DOEChart";
import DOETable from "../components/doe/DOETable";
import BusSummaryTable from "../components/doe/BusSummaryTable";
import MulticonductorDOEResults from "../components/doe/MulticonductorDOEResults";
import BenchmarkChart from "../components/doe/BenchmarkChart";
import BenchmarkV2Panel from "../components/doe/BenchmarkV2Panel";
import { Activity, Bolt, Gauge, Moon, Sun, Timer, Zap } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

const MIN_STEPS = 1;
const MAX_STEPS = 96;

const DEFAULT_PARAMS = {
  network_id: "case9",
  n_steps: 24,
  v_min: 0.95,
  v_max: 1.05,
  use_synthetic_profile: true,
  method: "iterative",
};

// Fallback catalog used if GET /api/algorithms is unavailable. Mirrors the
// backend two-engine catalog so the picker still works offline.
const FALLBACK_ALGORITHMS = [
  { id: "iterative", label: "Iterative", sub: "Bisection per bus · accurate, slower", engine: "pandapower", engineLabel: "Pandapower · balanced (time-series)", resultKind: "timeseries" },
  { id: "linearized", label: "Linearized PF", sub: "Sensitivity-based · fast, ideal for large grids", engine: "pandapower", engineLabel: "Pandapower · balanced (time-series)", resultKind: "timeseries" },
  { id: "opf", label: "OPF (AC)", sub: "Joint optimisation · most accurate, slowest", engine: "pandapower", engineLabel: "Pandapower · balanced (time-series)", resultKind: "timeseries" },
  { id: "model_free", label: "Model-free", sub: "Black-box random search · no Jacobian or OPF", engine: "pandapower", engineLabel: "Pandapower · balanced (time-series)", resultKind: "timeseries" },
  { id: "aimd", label: "AIMD (decentralised)", sub: "Additive-increase / multiplicative-decrease · per-phase", engine: "multiconductor", engineLabel: "Multiconductor · 3-phase (time-series)", resultKind: "timeseries" },
  { id: "linearized_pf", label: "Linearised PF (3-phase)", sub: "LinDistFlow sensitivities · per-phase", engine: "multiconductor", engineLabel: "Multiconductor · 3-phase (time-series)", resultKind: "timeseries" },
  { id: "icnn", label: "ICNN (convex surrogate)", sub: "Input-convex network feasibility oracle · per-phase", engine: "multiconductor", engineLabel: "Multiconductor · 3-phase (time-series)", resultKind: "timeseries" },
  { id: "traditional", label: "Traditional OE (proportional)", sub: "Team-Nando OE1-Ideal port · per-phase", engine: "multiconductor", engineLabel: "Multiconductor · 3-phase (time-series)", resultKind: "timeseries" },
];

const clampSteps = (value) => Math.min(MAX_STEPS, Math.max(MIN_STEPS, value));

// The generator-bus benchmark always runs four analytic methods (iterative +
// linearized power flow for the pandapower and multiconductor engines) plus an
// OpenDSS equivalent — five units of work in total.
const BENCHMARK_METHOD_LABELS = [
  "Iterative (pandapower)",
  "Linearized PF (pandapower)",
  "Iterative AIMD (multiconductor)",
  "Linearized PF (multiconductor)",
];
const BENCHMARK_UNIT_COUNT = BENCHMARK_METHOD_LABELS.length + 1;

const formatPct = (value) => {
  const v = Number(value);
  if (!Number.isFinite(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
};

const formatSeconds = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return "—";
  if (seconds < 1) return `${seconds.toFixed(3)}s`;
  return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
};

const formatEnergy = (value) => {
  const energy = Number(value);
  if (!Number.isFinite(energy)) return "—";
  return `${energy.toFixed(Math.abs(energy) < 10 ? 2 : 1)} MWh`;
};

export default function DOEDashboard() {
  const { theme, toggle: toggleTheme } = useTheme();
  const [networks, setNetworks] = useState([]);
  const [algorithms, setAlgorithms] = useState(FALLBACK_ALGORITHMS);
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [stepsInput, setStepsInput] = useState(String(DEFAULT_PARAMS.n_steps));
  const [networkInfo, setNetworkInfo] = useState(null);
  const [topology, setTopology] = useState(null);
  const [topologyLoading, setTopologyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [progress, setProgress] = useState(null); // { step, n_steps, last }
  const [runElapsedSeconds, setRunElapsedSeconds] = useState(0);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [benchmarkRows, setBenchmarkRows] = useState([]);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [benchmarkProgress, setBenchmarkProgress] = useState(null);
  const streamRef = useRef(null);
  const benchmarkRunRef = useRef(0);

  // Load networks
  useEffect(() => {
    fetchNetworks()
      .then(setNetworks)
      .catch(() => toast.error("Unable to load sample networks"));
  }, []);

  // Load algorithm catalog (falls back to the static list on failure)
  useEffect(() => {
    fetchAlgorithms()
      .then((data) => {
        if (Array.isArray(data?.algorithms) && data.algorithms.length) {
          setAlgorithms(data.algorithms);
        }
      })
      .catch(() => {
        /* keep FALLBACK_ALGORITHMS */
      });
  }, []);

  const methodGroups = useMemo(() => {
    const groups = {};
    for (const algo of algorithms) {
      const label = algo.engineLabel || algo.engine || "Algorithms";
      (groups[label] = groups[label] || []).push(algo);
    }
    return groups;
  }, [algorithms]);

  // Load network info on change
  useEffect(() => {
    if (!params.network_id) return;
    // Switching network invalidates the previous DOE result and topology
    setNetworkInfo(null);
    setTopology(null);
    setResult(null);
    setElapsed(null);
    setProgress(null);
    setRunElapsedSeconds(0);
    setBenchmarkRows([]);
    setBenchmarkProgress(null);
    setBenchmarkLoading(false);
    benchmarkRunRef.current += 1;
    if (streamRef.current) {
      streamRef.current.close();
      streamRef.current = null;
    }
    setLoading(false);
    setTopologyLoading(true);
    fetchNetworkInfo(params.network_id)
      .then(setNetworkInfo)
      .catch(() => setNetworkInfo(null));
    fetchNetworkTopology(params.network_id)
      .then(setTopology)
      .catch(() => setTopology(null))
      .finally(() => setTopologyLoading(false));
  }, [params.network_id]);

  const networkOptions = useMemo(() => {
    const groups = {};
    for (const n of networks) {
      (groups[n.category] = groups[n.category] || []).push(n);
    }
    return groups;
  }, [networks]);

  useEffect(() => {
    if (!loading) return undefined;
    const timer = window.setInterval(() => {
      setRunElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [loading]);

  const normalizeStepsInput = () => {
    const parsed = Number(stepsInput);
    const nextSteps =
      stepsInput.trim() === "" || !Number.isFinite(parsed)
        ? params.n_steps
        : clampSteps(Math.trunc(parsed));

    setStepsInput(String(nextSteps));
    setParams((p) => ({ ...p, n_steps: nextSteps }));
    return nextSteps;
  };

  const handleRun = async () => {
    const nSteps = normalizeStepsInput();
    const runParams = { ...params, n_steps: nSteps };

    benchmarkRunRef.current += 1;
    setBenchmarkLoading(false);
    setBenchmarkProgress(null);
    setLoading(true);
    setResult(null);
    setElapsed(null);
    setRunElapsedSeconds(0);
    const start = performance.now();
    if (streamRef.current) {
      streamRef.current.close();
      streamRef.current = null;
    }

    setProgress({ step: 0, n_steps: runParams.n_steps, last: null });
    streamRef.current = streamDOE(runParams, {
      onProgress: (evt) => {
        if (evt.phase === "start") {
          setProgress({ step: 0, n_steps: evt.n_steps, last: null });
        } else if (evt.phase === "step") {
          setProgress({ step: evt.step, n_steps: evt.n_steps, last: evt });
        }
      },
      onResult: (data) => {
        setResult(data);
        setElapsed(((performance.now() - start) / 1000).toFixed(1));
        setLoading(false);
        setProgress(null);
        setRunElapsedSeconds(0);
        toast.success(
          `DOE computed for ${data.meta.id} (${data.meta.n_steps} steps, ${data.bus_summary.length} buses)`,
        );
      },
      onError: (err) => {
        setLoading(false);
        setProgress(null);
        setRunElapsedSeconds(0);
        toast.error(err?.message || "Calculation failed");
      },
    });
  };

  const handleBenchmarkRun = async () => {
    const nSteps = normalizeStepsInput();
    const runParams = { ...params, n_steps: nSteps };
    const runId = benchmarkRunRef.current + 1;
    benchmarkRunRef.current = runId;

    if (streamRef.current) {
      streamRef.current.close();
      streamRef.current = null;
    }
    setLoading(false);
    setProgress(null);
    setRunElapsedSeconds(0);
    setBenchmarkLoading(true);
    setBenchmarkRows([]);
    setBenchmarkData(null);
    setBenchmarkProgress({ complete: 0, total: BENCHMARK_UNIT_COUNT, method: null });

    try {
      const data = await runBenchmark({
        network_id: runParams.network_id,
        n_steps: nSteps,
        v_min: runParams.v_min,
        v_max: runParams.v_max,
        use_synthetic_profile: runParams.use_synthetic_profile,
        include_opendss: true,
      });
      if (benchmarkRunRef.current !== runId) return;

      const methods = Array.isArray(data?.methods) ? data.methods : [];
      const rows = methods.map((m) => ({
        id: m.id,
        method: m.id,
        methodLabel: m.label || m.id,
        engine: m.engine || "unknown",
        engineLabel: m.engineLabel || m.engine || "Algorithm",
        status: m.status,
        solveTimeSeconds: m.solve_time_seconds,
        netImportMwh: m.net_import_mwh,
        netExportMwh: m.net_export_mwh,
        nSteps: m.n_steps ?? nSteps,
        nResolvedGenBuses: m.n_resolved_gen_buses,
        nTargetBuses: m.n_target_buses,
        coverageNote: m.coverage_note,
        error: m.error,
      }));

      setBenchmarkRows(rows);
      setBenchmarkData(data);
      setBenchmarkProgress({ complete: BENCHMARK_UNIT_COUNT, total: BENCHMARK_UNIT_COUNT, method: null });

      const failed = methods.filter((m) => m.status === "failed").length;
      if (failed) {
        toast.error(`Benchmark completed with ${failed} failed method${failed === 1 ? "" : "s"}`);
      } else {
        toast.success(`Benchmark completed for ${methods.length} DOE methods`);
      }
    } catch (err) {
      if (benchmarkRunRef.current !== runId) return;
      toast.error(
        err?.response?.data?.detail || err?.message || "Benchmark failed",
      );
    } finally {
      if (benchmarkRunRef.current === runId) {
        setBenchmarkLoading(false);
      }
    }
  };

  const timeSeries = result ? result.time_series || [] : [];
  const hasTimeSeries = timeSeries.length > 0;
  const snapshotDetail = result?.snapshot || null;
  const totalImportMwh = timeSeries.reduce((s, t) => s + t.total_import_mw, 0);
  const totalExportMwh = timeSeries.reduce((s, t) => s + t.total_export_mw, 0);
  const peakImport = timeSeries.length
    ? Math.max(0, ...timeSeries.map((t) => t.total_import_mw))
    : 0;
  const peakExport = timeSeries.length
    ? Math.max(0, ...timeSeries.map((t) => t.total_export_mw))
    : 0;
  const completedSteps = progress?.step ?? 0;
  const totalSteps = progress?.n_steps ?? params.n_steps;
  const solveTimeSource = result?.meta?.solve_time_seconds ?? elapsed;
  const solveTimeSeconds =
    solveTimeSource === undefined || solveTimeSource === null
      ? null
      : Number(solveTimeSource);
  const solveTimeLabel =
    solveTimeSeconds !== null && Number.isFinite(solveTimeSeconds)
      ? `${solveTimeSeconds.toFixed(solveTimeSeconds < 10 ? 1 : 0)}s`
      : "—";
  const activeStep = totalSteps
    ? Math.min(Math.max(completedSteps + 1, 1), totalSteps)
    : 0;
  const progressValue = totalSteps ? (completedSteps / totalSteps) * 100 : 0;
  const progressPercent = Math.round(progressValue);
  const isAwaitingStep = loading && completedSteps === 0;
  const benchmarkTotal = benchmarkProgress?.total ?? BENCHMARK_UNIT_COUNT;
  const benchmarkComplete = benchmarkProgress?.complete ?? benchmarkRows.length;
  const benchmarkProgressValue = benchmarkTotal
    ? (benchmarkComplete / benchmarkTotal) * 100
    : 0;
  const benchmarkPercent = Math.round(benchmarkProgressValue);
  const benchmarkActiveIndex = benchmarkTotal
    ? Math.min(benchmarkComplete + 1, benchmarkTotal)
    : 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-9 grid place-items-center bg-primary text-primary-foreground rounded-[2px]">
              <Bolt className="size-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                Pandapower &amp; Multiconductor · v3
              </div>
              <h1 className="text-xl font-semibold tracking-tight">
                Dynamic Operating Envelope Studio
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground font-mono">
              <span className="inline-block size-1.5 bg-[#16A34A] rounded-full" />
              grid solver online
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              className="size-9 grid place-items-center border border-border rounded-[2px] hover:bg-secondary transition-colors"
            >
              {theme === "dark" ? (
                <Sun className="size-4" data-testid="theme-icon-sun" />
              ) : (
                <Moon className="size-4" data-testid="theme-icon-moon" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hero / band */}
      <section
        className="relative border-b border-border bg-secondary/40 grid-bg"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-3">
              Voltage-constrained DOE · balanced &amp; 3-phase distribution grids
            </div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold leading-tight">
              Benchmarking for Model-free & Model-based Dynamic Operating Envelope algorithms.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
              Pick a pandapower or multiconductor network and any of eight DOE
              algorithms. Every solver sweeps the configured time horizon and
              charts import/export headroom; 3-phase methods also retain a
              per-phase envelope detail for the final step.
            </p>
          </div>
        </div>
      </section>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b border-border bg-background" data-testid="page-tabs">
          <TabsList className="max-w-7xl mx-auto flex h-11 justify-start rounded-none bg-transparent p-0 text-foreground">
            <TabsTrigger
              value="dashboard"
              data-testid="dashboard-tab"
              className="h-11 min-w-36 rounded-none border-r border-border px-8 font-medium shadow-none data-[state=active]:bg-secondary data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              Dashboard
            </TabsTrigger>
            <TabsTrigger
              value="benchmark"
              data-testid="benchmark-tab"
              className="h-11 min-w-36 rounded-none border-r border-border px-8 font-medium shadow-none data-[state=active]:bg-secondary data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              Benchmark
            </TabsTrigger>
            <TabsTrigger
              value="benchmark-v2"
              data-testid="benchmark-v2-tab"
              className="h-11 min-w-40 rounded-none border-r border-border px-8 font-medium shadow-none data-[state=active]:bg-secondary data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              Benchmark v2.0
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="dashboard" className="m-0">
          {/* Control panel */}
          <section className="border-b border-border" data-testid="control-panel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-3">
              <Label htmlFor="network-select" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Sample Network
              </Label>
              <div className="mt-2">
                <Select
                  value={params.network_id}
                  onValueChange={(v) => setParams((p) => ({ ...p, network_id: v }))}
                >
                  <SelectTrigger
                    id="network-select"
                    data-testid="network-selector"
                    className="rounded-[2px] h-11 font-mono text-sm"
                  >
                    <SelectValue placeholder="Choose a network…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-[2px]">
                    {Object.entries(networkOptions).map(([cat, list]) => (
                      <SelectGroup key={cat}>
                        <SelectLabel className="text-[10px] uppercase tracking-[0.18em]">
                          {cat}
                        </SelectLabel>
                        {list.map((n) => (
                          <SelectItem
                            key={n.id}
                            value={n.id}
                            data-testid={`network-option-${n.id}`}
                            className="font-mono text-sm"
                          >
                            {n.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="method-select" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Method
              </Label>
              <div className="mt-2">
                <Select
                  value={params.method}
                  onValueChange={(v) => setParams((p) => ({ ...p, method: v }))}
                >
                  <SelectTrigger
                    id="method-select"
                    data-testid="method-selector"
                    className="rounded-[2px] h-11 font-mono text-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-[2px]">
                    {Object.entries(methodGroups).map(([engineLabel, list]) => (
                      <SelectGroup key={engineLabel}>
                        <SelectLabel className="text-[10px] uppercase tracking-[0.18em]">
                          {engineLabel}
                        </SelectLabel>
                        {list.map((m) => (
                          <SelectItem
                            key={m.id}
                            value={m.id}
                            data-testid={`method-option-${m.id}`}
                            className="font-mono text-sm py-2"
                          >
                            <div className="flex flex-col">
                              <span>{m.label}</span>
                              <span className="text-[10px] text-muted-foreground tracking-tight">
                                {m.sub}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="md:col-span-1">
              <Label htmlFor="n-steps" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Steps
              </Label>
              <Input
                id="n-steps"
                data-testid="time-steps-input"
                type="number"
                min={MIN_STEPS}
                max={MAX_STEPS}
                inputMode="numeric"
                value={stepsInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setStepsInput(value);
                  if (value.trim() === "") return;

                  const parsed = Number(value);
                  if (!Number.isFinite(parsed)) return;

                  setParams((p) => ({
                    ...p,
                    n_steps: clampSteps(Math.trunc(parsed)),
                  }));
                }}
                onBlur={normalizeStepsInput}
                className="mt-2 h-11 rounded-[2px] font-mono"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="v-min" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                V<sub>min</sub> (p.u.)
              </Label>
              <Input
                id="v-min"
                data-testid="v-min-input"
                type="number"
                step="0.01"
                value={params.v_min}
                onChange={(e) =>
                  setParams((p) => ({ ...p, v_min: Number(e.target.value) }))
                }
                className="mt-2 h-11 rounded-[2px] font-mono"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="v-max" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                V<sub>max</sub> (p.u.)
              </Label>
              <Input
                id="v-max"
                data-testid="v-max-input"
                type="number"
                step="0.01"
                value={params.v_max}
                onChange={(e) =>
                  setParams((p) => ({ ...p, v_max: Number(e.target.value) }))
                }
                className="mt-2 h-11 rounded-[2px] font-mono"
              />
            </div>

            <div className="md:col-span-2 flex">
              <Button
                data-testid="calculate-doe-button"
                onClick={handleRun}
                disabled={loading || !params.network_id}
                className="w-full h-11 rounded-[2px] bg-primary text-primary-foreground hover:bg-primary/90 font-semibold tracking-wide uppercase text-xs"
              >
                {loading
                  ? progress?.n_steps
                    ? `Solving ${activeStep}/${totalSteps}…`
                    : "Calculating…"
                  : "Calculate DOE"}
              </Button>
            </div>
          </div>

          {/* Network info row */}
          <div
            className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border rounded-[2px] overflow-hidden"
            data-testid="network-info-grid"
          >
            <InfoCell label="Buses" value={networkInfo?.n_bus ?? "—"} />
            <InfoCell label="Lines" value={networkInfo?.n_line ?? "—"} />
            <InfoCell label="Transformers" value={networkInfo?.n_trafo ?? "—"} />
            <InfoCell label="Generators" value={networkInfo?.n_gen ?? "—"} />
            <InfoCell label="Loads" value={networkInfo?.n_load ?? "—"} />
            <InfoCell
              label="Σ load (MW)"
              value={networkInfo ? networkInfo.total_load_mw.toFixed(1) : "—"}
            />
          </div>
        </div>

        {/* loading scan line + progress */}
        {loading && (
          <>
            <div
              className="relative h-[2px] w-full overflow-hidden bg-secondary scan-line"
              data-testid="loading-bar"
            />
            {progress && (
              <div
                className="border-t border-border bg-secondary/30"
                data-testid="progress-panel"
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">
                      <span>
                        Solving · step{" "}
                        <span
                          className="text-foreground font-mono"
                          data-testid="progress-current-step"
                        >
                          {activeStep}
                        </span>{" "}
                        / {totalSteps}
                      </span>
                      <span className="font-mono text-foreground">
                        {progressPercent}% · {runElapsedSeconds}s
                      </span>
                    </div>
                    <Progress
                      value={progressValue}
                      indeterminate={isAwaitingStep}
                      className="h-1.5 rounded-[2px] bg-secondary"
                      data-testid="progress-bar"
                    />
                    {progress.last ? (
                      <div className="mt-2 text-[11px] font-mono text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
                        <span>
                          import:{" "}
                          <span className="text-[var(--doe-import)] font-semibold">
                            {(progress.last.total_import_mw ?? 0).toFixed(1)} MW
                          </span>
                        </span>
                        <span>
                          export:{" "}
                          <span className="text-[var(--doe-export)] font-semibold">
                            {(progress.last.total_export_mw ?? 0).toFixed(1)} MW
                          </span>
                        </span>
                        <span>
                          V<sub>min</sub>: {progress.last.min_voltage ?? "—"}
                        </span>
                        <span>
                          V<sub>max</sub>: {progress.last.max_voltage ?? "—"}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] font-mono text-muted-foreground">
                        solver active · waiting for first completed time step
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Results */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="results-section">
        {/* Topology — always shown when a network is loaded */}
        <section className="mb-10" data-testid="topology-section">
          <div className="mb-3 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                Section · 00
              </div>
              <h3 className="text-2xl tracking-tight font-semibold mt-1">
                Network Topology
              </h3>
            </div>
          </div>
          {topologyLoading && (
            <div
              className="border border-dashed border-border rounded-[2px] p-12 text-center bg-secondary/20 text-xs font-mono text-muted-foreground"
              data-testid="topology-loading"
            >
              loading topology…
            </div>
          )}
          {!topologyLoading && topology && (
            <NetworkTopology
              topology={topology}
              doeResult={hasTimeSeries ? result : null}
            />
          )}
          {!topologyLoading && !topology && (
            <div className="border border-dashed border-border rounded-[2px] p-8 text-center bg-secondary/20 text-xs font-mono text-muted-foreground">
              topology unavailable
            </div>
          )}
        </section>

        {!result && !loading && <EmptyState />}

        {result && hasTimeSeries && (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border rounded-[2px] overflow-hidden mb-8" data-testid="kpi-strip">
              <KPI
                icon={<Zap className="size-4" />}
                label="Peak Import Capacity"
                value={`${peakImport.toFixed(1)} MW`}
                accent="text-[var(--doe-import)]"
                testid="kpi-peak-import"
              />
              <KPI
                icon={<Activity className="size-4" />}
                label="Peak Export Capacity"
                value={`${peakExport.toFixed(1)} MW`}
                accent="text-[var(--doe-export)]"
                testid="kpi-peak-export"
              />
              <KPI
                icon={<Timer className="size-4" />}
                label="Solve Time"
                value={solveTimeLabel}
                testid="kpi-solve-time"
              />
              <KPI
                icon={<Gauge className="size-4" />}
                label="Daily Import Energy"
                value={`${totalImportMwh.toFixed(0)} MWh`}
                testid="kpi-import-energy"
              />
              <KPI
                icon={<Gauge className="size-4" />}
                label="Daily Export Energy"
                value={`${totalExportMwh.toFixed(0)} MWh`}
                testid="kpi-export-energy"
              />
            </div>

            <div className="mb-2 flex items-end justify-between" data-testid="chart-header">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                  Section · 01
                </div>
                <h3 className="text-2xl tracking-tight font-semibold mt-1">
                  Aggregated DOE — Import vs Export
                </h3>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  network={result.meta.id} · method={result.meta.method || "iterative"} · steps={result.meta.n_steps} · V∈[
                  {result.meta.v_min}, {result.meta.v_max}] p.u. · solved in {solveTimeLabel}
                </div>
              </div>
            </div>

            <DOEChart timeSeries={result.time_series} />

            <div className="mt-10 mb-2 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                  Section · 02
                </div>
                <h3 className="text-2xl tracking-tight font-semibold mt-1">
                  Per-time-step Results
                </h3>
              </div>
            </div>
            <DOETable timeSeries={result.time_series} />

            <div className="mt-10 mb-2 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                  Section · 03
                </div>
                <h3 className="text-2xl tracking-tight font-semibold mt-1">
                  Bus-level Envelope Summary
                </h3>
              </div>
            </div>
            <BusSummaryTable rows={result.bus_summary} />

            {snapshotDetail && (
              <>
                <div className="mt-10 mb-2 flex items-end justify-between" data-testid="mc-result-header">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                      Section · 04
                    </div>
                    <h3 className="text-2xl tracking-tight font-semibold mt-1">
                      Per-phase Envelope Detail
                    </h3>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      final sweep step · method={snapshotDetail.meta?.method || snapshotDetail.algorithm} · V∈[
                      {snapshotDetail.meta?.v_min ?? snapshotDetail.params?.vMin},{" "}
                      {snapshotDetail.meta?.v_max ?? snapshotDetail.params?.vMax}] p.u.
                    </div>
                  </div>
                </div>
                <MulticonductorDOEResults result={snapshotDetail} />
              </>
            )}
          </>
        )}
          </main>
        </TabsContent>

        <TabsContent value="benchmark" className="m-0">
          <section className="border-b border-border" data-testid="benchmark-control-panel">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-3">
                  <Label htmlFor="benchmark-network-select" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    Sample Network
                  </Label>
                  <div className="mt-2">
                    <Select
                      value={params.network_id}
                      onValueChange={(v) => setParams((p) => ({ ...p, network_id: v }))}
                    >
                      <SelectTrigger
                        id="benchmark-network-select"
                        data-testid="benchmark-network-selector"
                        className="rounded-[2px] h-11 font-mono text-sm"
                      >
                        <SelectValue placeholder="Choose a network…" />
                      </SelectTrigger>
                      <SelectContent className="rounded-[2px]">
                        {Object.entries(networkOptions).map(([cat, list]) => (
                          <SelectGroup key={cat}>
                            <SelectLabel className="text-[10px] uppercase tracking-[0.18em]">
                              {cat}
                            </SelectLabel>
                            {list.map((n) => (
                              <SelectItem
                                key={n.id}
                                value={n.id}
                                data-testid={`benchmark-network-option-${n.id}`}
                                className="font-mono text-sm"
                              >
                                {n.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    Methods
                  </Label>
                  <div
                    className="mt-2 h-11 rounded-[2px] border border-input bg-background px-3 py-2 font-mono text-sm flex items-center"
                    data-testid="benchmark-method-count"
                  >
                    4 PF methods + OpenDSS
                  </div>
                </div>

                <div className="md:col-span-1">
                  <Label htmlFor="benchmark-n-steps" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    Steps
                  </Label>
                  <Input
                    id="benchmark-n-steps"
                    data-testid="benchmark-time-steps-input"
                    type="number"
                    min={MIN_STEPS}
                    max={MAX_STEPS}
                    inputMode="numeric"
                    value={stepsInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setStepsInput(value);
                      if (value.trim() === "") return;

                      const parsed = Number(value);
                      if (!Number.isFinite(parsed)) return;

                      setParams((p) => ({
                        ...p,
                        n_steps: clampSteps(Math.trunc(parsed)),
                      }));
                    }}
                    onBlur={normalizeStepsInput}
                    className="mt-2 h-11 rounded-[2px] font-mono"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="benchmark-v-min" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    V<sub>min</sub> (p.u.)
                  </Label>
                  <Input
                    id="benchmark-v-min"
                    data-testid="benchmark-v-min-input"
                    type="number"
                    step="0.01"
                    value={params.v_min}
                    onChange={(e) =>
                      setParams((p) => ({ ...p, v_min: Number(e.target.value) }))
                    }
                    className="mt-2 h-11 rounded-[2px] font-mono"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="benchmark-v-max" className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    V<sub>max</sub> (p.u.)
                  </Label>
                  <Input
                    id="benchmark-v-max"
                    data-testid="benchmark-v-max-input"
                    type="number"
                    step="0.01"
                    value={params.v_max}
                    onChange={(e) =>
                      setParams((p) => ({ ...p, v_max: Number(e.target.value) }))
                    }
                    className="mt-2 h-11 rounded-[2px] font-mono"
                  />
                </div>

                <div className="md:col-span-2 flex">
                  <Button
                    data-testid="run-benchmark-button"
                    onClick={handleBenchmarkRun}
                    disabled={benchmarkLoading || !params.network_id}
                    className="w-full h-11 rounded-[2px] bg-primary text-primary-foreground hover:bg-primary/90 font-semibold tracking-wide uppercase text-xs"
                  >
                    {benchmarkLoading
                      ? `Running ${benchmarkActiveIndex}/${benchmarkTotal}…`
                      : "Run Benchmark"}
                  </Button>
                </div>
              </div>

              <div
                className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border rounded-[2px] overflow-hidden"
                data-testid="benchmark-network-info-grid"
              >
                <InfoCell label="Buses" value={networkInfo?.n_bus ?? "—"} />
                <InfoCell label="Lines" value={networkInfo?.n_line ?? "—"} />
                <InfoCell label="Transformers" value={networkInfo?.n_trafo ?? "—"} />
                <InfoCell label="Generators" value={networkInfo?.n_gen ?? "—"} />
                <InfoCell label="Loads" value={networkInfo?.n_load ?? "—"} />
                <InfoCell
                  label="Σ load (MW)"
                  value={networkInfo ? networkInfo.total_load_mw.toFixed(1) : "—"}
                />
              </div>
            </div>

            {benchmarkLoading && (
              <>
                <div
                  className="relative h-[2px] w-full overflow-hidden bg-secondary scan-line"
                  data-testid="benchmark-loading-bar"
                />
                <div
                  className="border-t border-border bg-secondary/30"
                  data-testid="benchmark-progress-panel"
                >
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">
                        <span>
                          Benchmarking · method{" "}
                          <span className="text-foreground font-mono">
                            {benchmarkActiveIndex}
                          </span>{" "}
                          / {benchmarkTotal}
                        </span>
                        <span className="font-mono text-foreground">
                          {benchmarkPercent}%
                        </span>
                      </div>
                      <Progress
                        value={benchmarkProgressValue}
                        indeterminate={benchmarkComplete === 0}
                        className="h-1.5 rounded-[2px] bg-secondary"
                        data-testid="benchmark-progress-bar"
                      />
                      <div className="mt-2 text-[11px] font-mono text-muted-foreground">
                        {benchmarkProgress?.method
                          ? `running ${benchmarkProgress.method}`
                          : "benchmark complete"}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="benchmark-section">
            <section className="mb-10" data-testid="benchmark-topology-section">
              <div className="mb-3 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                    Section · 00
                  </div>
                  <h3 className="text-2xl tracking-tight font-semibold mt-1">
                    Network Topology
                  </h3>
                </div>
              </div>
              {topologyLoading && (
                <div
                  className="border border-dashed border-border rounded-[2px] p-12 text-center bg-secondary/20 text-xs font-mono text-muted-foreground"
                  data-testid="benchmark-topology-loading"
                >
                  loading topology…
                </div>
              )}
              {!topologyLoading && topology && (
                <NetworkTopology topology={topology} doeResult={null} />
              )}
              {!topologyLoading && !topology && (
                <div className="border border-dashed border-border rounded-[2px] p-8 text-center bg-secondary/20 text-xs font-mono text-muted-foreground">
                  topology unavailable
                </div>
              )}
            </section>

            {!benchmarkRows.length && !benchmarkLoading && <BenchmarkEmptyState />}

            {benchmarkRows.length > 0 && (
              <>
                <div className="mb-2 flex items-end justify-between" data-testid="benchmark-results-header">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                      Section · 01
                    </div>
                    <h3 className="text-2xl tracking-tight font-semibold mt-1">
                      Benchmark Summary
                    </h3>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      network={params.network_id} · DOE at generator buses ·
                      nameplate-limited · steps={params.n_steps} · V∈[
                      {params.v_min}, {params.v_max}] p.u.
                    </div>
                    {benchmarkData?.meta && (
                      <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                        generator buses ={" "}
                        {Array.isArray(benchmarkData.meta.target_buses) && benchmarkData.meta.target_buses.length
                          ? benchmarkData.meta.target_buses.join(", ")
                          : (benchmarkData.meta.n_target_buses ?? "—")}{" "}
                        · profiles: residential load + solar PV generation
                      </div>
                    )}
                  </div>
                </div>
                <BenchmarkSummaryTable rows={benchmarkRows} />

                {benchmarkData?.opendss && (
                  <>
                    <div className="mt-10 mb-2 flex items-end justify-between" data-testid="benchmark-opendss-header">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                          Section · 02
                        </div>
                        <h3 className="text-2xl tracking-tight font-semibold mt-1">
                          OpenDSS Comparison
                        </h3>
                        <div className="text-xs text-muted-foreground mt-1 font-mono">
                          {benchmarkData.opendss.available
                            ? "Each method's net envelope energy compared against the OpenDSS equivalent network model (reference)."
                            : "OpenDSS equivalent unavailable."}
                        </div>
                      </div>
                    </div>
                    <OpenDSSReferenceCard opendss={benchmarkData.opendss} />
                    {benchmarkData.opendss.available &&
                      Array.isArray(benchmarkData.comparison) &&
                      benchmarkData.comparison.length > 0 && (
                        <div className="mt-4">
                          <BenchmarkComparisonTable
                            comparison={benchmarkData.comparison}
                          />
                        </div>
                      )}
                  </>
                )}

                <div className="mt-10 mb-2 flex items-end justify-between" data-testid="benchmark-charts-header">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                      Section · 03
                    </div>
                    <h3 className="text-2xl tracking-tight font-semibold mt-1">
                      Performance Comparison
                    </h3>
                  </div>
                </div>
                <BenchmarkChart rows={benchmarkRows} />

                <div className="mt-10 mb-2 flex items-end justify-between" data-testid="benchmark-detail-header">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                      Section · 04
                    </div>
                    <h3 className="text-2xl tracking-tight font-semibold mt-1">
                      Per-Method Detail
                    </h3>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      Hourly envelope time series and per-bus summary for each
                      method.
                    </div>
                  </div>
                </div>
                <BenchmarkDetailPanels
                  methods={benchmarkData?.methods || []}
                  opendss={benchmarkData?.opendss}
                />
              </>
            )}
          </main>
        </TabsContent>

        <TabsContent value="benchmark-v2" className="m-0">
          <BenchmarkV2Panel />
        </TabsContent>
      </Tabs>

      <footer className="border-t border-border mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-muted-foreground font-mono flex items-center justify-between">
          <span>pandapower &amp; multiconductor &amp; OpenDSS · DOE solvers · iterative · linearized · OPF · model-free · AIMD · ICNN · traditional</span>
          <span className="opacity-70">v=1.0</span>
        </div>
      </footer>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div className="bg-background px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="mt-1 text-base font-mono font-medium">{value}</div>
    </div>
  );
}

function KPI({ icon, label, value, accent = "text-foreground", testid }) {
  return (
    <div className="bg-background px-5 py-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
        {icon}
        {label}
      </div>
      <div className={`mt-2 text-2xl font-mono font-semibold tracking-tight ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="border border-dashed border-border rounded-[2px] p-12 text-center bg-secondary/30"
      data-testid="empty-state"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
        idle
      </div>
      <h3 className="mt-3 text-xl font-semibold tracking-tight">
        Configure parameters and press <span className="font-mono">Calculate DOE</span>
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        Defaults: 24 hourly time steps, V ∈ [0.95, 1.05] p.u., synthetic
        residential load profile.
      </p>
    </div>
  );
}

function BenchmarkSummaryTable({ rows }) {
  return (
    <div
      className="border border-border rounded-[2px] overflow-hidden"
      data-testid="benchmark-results-table"
    >
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/60 hover:bg-secondary/60">
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em]">
              Method
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em]">
              Engine
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em]">
              Status
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Solve Time
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Net Import
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Net Export
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Gen Buses
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Steps
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="px-4 py-3">
                <div className="font-medium">{row.methodLabel}</div>
                <div className="text-[11px] font-mono text-muted-foreground">
                  {row.method}
                </div>
              </TableCell>
              <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                {row.engineLabel}
              </TableCell>
              <TableCell className="px-4 py-3">
                <span
                  className={
                    row.status === "complete"
                      ? "text-[var(--doe-export)] font-mono text-xs uppercase tracking-[0.12em]"
                      : "text-destructive font-mono text-xs uppercase tracking-[0.12em]"
                  }
                  title={row.error || undefined}
                >
                  {row.status}
                </span>
                {row.error && (
                  <div className="mt-1 max-w-xs truncate text-[11px] text-muted-foreground">
                    {row.error}
                  </div>
                )}
                {!row.error &&
                  row.status === "complete" &&
                  row.nResolvedGenBuses === 0 && (
                    <div
                      className="mt-1 max-w-xs text-[11px] text-amber-600 dark:text-amber-400"
                      title={row.coverageNote || undefined}
                    >
                      no DER buses reachable
                    </div>
                  )}
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-mono">
                {formatSeconds(row.solveTimeSeconds)}
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-mono text-[var(--doe-import)]">
                {formatEnergy(row.netImportMwh)}
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-mono text-[var(--doe-export)]">
                {formatEnergy(row.netExportMwh)}
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-mono">
                {row.nResolvedGenBuses ?? row.nTargetBuses ?? "—"}
                {row.nTargetBuses != null &&
                  row.nResolvedGenBuses != null &&
                  row.nResolvedGenBuses !== row.nTargetBuses && (
                    <span className="text-muted-foreground">
                      /{row.nTargetBuses}
                    </span>
                  )}
              </TableCell>
              <TableCell className="px-4 py-3 text-right font-mono">
                {row.nSteps ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BenchmarkEmptyState() {
  return (
    <div
      className="border border-dashed border-border rounded-[2px] p-12 text-center bg-secondary/30"
      data-testid="benchmark-empty-state"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
        idle
      </div>
      <h3 className="mt-3 text-xl font-semibold tracking-tight">
        Configure parameters and press <span className="font-mono">Run Benchmark</span>
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        Computes DOE at generator buses only (nameplate-limited) over 24h with
        residential-load and solar-PV profiles. Runs iterative and linearized
        power flow for both the pandapower and multiconductor engines, then
        compares against an OpenDSS equivalent network model.
      </p>
    </div>
  );
}

function OpenDSSReferenceCard({ opendss }) {
  if (!opendss?.available) {
    return (
      <div
        className="border border-dashed border-border rounded-[2px] p-6 bg-secondary/20 text-sm text-muted-foreground"
        data-testid="benchmark-opendss-unavailable"
      >
        OpenDSS equivalent unavailable
        {opendss?.reason ? ` — ${opendss.reason}` : ""}.
      </div>
    );
  }
  if (opendss.status === "failed") {
    return (
      <div
        className="border border-destructive/40 rounded-[2px] p-6 bg-destructive/5 text-sm text-destructive"
        data-testid="benchmark-opendss-failed"
      >
        OpenDSS equivalent failed: {opendss.error || "unknown error"}
      </div>
    );
  }
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border rounded-[2px] overflow-hidden"
      data-testid="benchmark-opendss-card"
    >
      <InfoCell label="Engine" value="OpenDSS · 3-phase" />
      <InfoCell label="Solve Time" value={formatSeconds(opendss.solve_time_seconds)} />
      <InfoCell label="Net Import" value={formatEnergy(opendss.net_import_mwh)} />
      <InfoCell label="Net Export" value={formatEnergy(opendss.net_export_mwh)} />
    </div>
  );
}

function BenchmarkComparisonTable({ comparison }) {
  return (
    <div
      className="border border-border rounded-[2px] overflow-hidden"
      data-testid="benchmark-comparison-table"
    >
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary/60 hover:bg-secondary/60">
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em]">
              Method
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Export vs DSS
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Import vs DSS
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Export RMSE
            </TableHead>
            <TableHead className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-right">
              Import RMSE
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {comparison.map((c) => (
            <TableRow key={c.method}>
              <TableCell className="px-4 py-3">
                <div className="font-medium">{c.label}</div>
                <div className="text-[11px] font-mono text-muted-foreground">
                  {c.engine}
                </div>
              </TableCell>
              {c.comparable ? (
                <>
                  <TableCell className="px-4 py-3 text-right font-mono">
                    {formatPct(c.export_pct_err)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right font-mono">
                    {formatPct(c.import_pct_err)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right font-mono">
                    {c.export_rmse_mw != null ? `${c.export_rmse_mw} MW` : "—"}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right font-mono">
                    {c.import_rmse_mw != null ? `${c.import_rmse_mw} MW` : "—"}
                  </TableCell>
                </>
              ) : (
                <TableCell
                  colSpan={4}
                  className="px-4 py-3 text-[12px] text-amber-600 dark:text-amber-400"
                >
                  {c.note || "Not comparable to OpenDSS for this network."}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BenchmarkDetailPanels({ methods, opendss }) {
  const panels = [...(methods || [])];
  if (opendss?.available && opendss.status === "complete") {
    panels.push({
      id: "opendss",
      label: opendss.label || "OpenDSS equivalent",
      engineLabel: opendss.engineLabel || "OpenDSS · 3-phase",
      status: "complete",
      time_series: opendss.time_series || [],
      bus_summary: opendss.bus_summary || [],
    });
  }
  const [active, setActive] = useState(panels[0]?.id || null);
  const current = panels.find((p) => p.id === active) || panels[0];

  if (!panels.length) return null;

  return (
    <div data-testid="benchmark-detail-panels">
      <div className="flex flex-wrap gap-2 mb-4">
        {panels.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActive(p.id)}
            className={`px-3 py-2 rounded-[2px] border text-xs font-mono tracking-wide transition-colors ${
              (current?.id || panels[0]?.id) === p.id
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border bg-background text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`benchmark-detail-tab-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {current && (
        <div className="space-y-6">
          <div className="text-[11px] font-mono text-muted-foreground">
            {current.engineLabel}
          </div>
          {Array.isArray(current.time_series) && current.time_series.length > 0 ? (
            <DOETable timeSeries={current.time_series} />
          ) : (
            <div className="border border-dashed border-border rounded-[2px] p-6 text-center text-xs font-mono text-muted-foreground">
              no time-series data
            </div>
          )}
          {Array.isArray(current.bus_summary) && current.bus_summary.length > 0 && (
            <BusSummaryTable rows={current.bus_summary} />
          )}
        </div>
      )}
    </div>
  );
}
